import React, { useState } from 'react';
import { Button, Badge, Tabs } from 'antd';
import { FileTextOutlined, VideoCameraOutlined, CheckCircleOutlined, CodeOutlined } from '@ant-design/icons';
import DialogBox, {
  Section,
  SectionTitle,
  SectionContent,
  MetadataItem,
  MetadataLabel,
  MetadataValue,
  QuestionItem,
  QuestionNumber,
  QuestionText,
  QuestionScenario,
  OptionsList,
  OptionItem,
  CorrectBadge,
  MetadataList,
  MetadataTag,
  CompetenciesList,
  CompetencyItem
} from '../DialogBox';
import styled from 'styled-components';
import { COLORS } from 'theme';

const { TabPane } = Tabs;

// Additional styled components for this component
const AssessmentHeader = styled.div`
  background-color: #f8fafc;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 24px;
  border-left: 4px solid ${COLORS.primary};
`;

const AssessmentTitle = styled.h2`
  color: ${COLORS.primary};
  margin: 0 0 16px 0;
  font-size: 24px;
  font-weight: 700;
`;

const AssessmentMetadata = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
  }
  
  .ant-tabs-tab {
    padding: 12px 16px;
    font-size: 16px;
  }
  
  .ant-tabs-tab-active {
    font-weight: 600;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionDescription = styled.p`
  color: #64748b;
  margin-bottom: 24px;
  font-size: 15px;
`;

const QuestionCount = styled.div`
  background-color: ${COLORS.primary};
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
`;

// Default descriptions if not provided via props
const defaultQuestionTypeDescriptions = {
  video: "Video Questions: Scenario-based questions suitable for a video response.",
  subjective: "Subjective Questions: Open-ended questions that require a written answer.",
  mcq: "MCQ Questions: Multiple-choice questions with options.",
  coding: "Coding Questions: Programming problems with test cases to solve."
};

/**
 * QuestionDisplay component for displaying assessment questions
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.visible - Whether the dialog is visible
 * @param {function} props.onClose - Function to call when dialog is closed
 * @param {Object} props.questions - The questions data object
 * @param {Object} props.questionTypeDescriptions - Descriptions for different question types
 */
const QuestionDisplay = ({ 
  visible, 
  onClose, 
  questions,
  questionTypeDescriptions = defaultQuestionTypeDescriptions
}) => {
  const [activeTab, setActiveTab] = useState('metadata');
  
  if (!questions) return null;
  
  // Extract data from the questions object
  const meta = questions.assessment_metadata || {};
  const mcq = questions.section_a_multiple_choice || {};
  const written = questions.section_b_written_responses || {};
  const video = questions.section_c_video_response || {};
  const coding = questions.section_d_coding || {};
  
  // Calculate dynamic duration based on question counts
  const calculateDuration = () => {
    const mcqCount = mcq.questions?.length || 0;
    const subjectiveCount = written.questions?.length || 0;
    const videoRaw = video.questions || video.question;
    const videoCount = videoRaw ? (Array.isArray(videoRaw) ? videoRaw.length : 1) : 0;
    const codingCount = coding.questions?.length || 0;
    const totalMinutes = (mcqCount * 1.5) + (subjectiveCount * 5) + (videoCount * 3) + (codingCount * 10);
    return Math.max(10, Math.ceil(totalMinutes / 5) * 5);
  };
  const calculatedDuration = calculateDuration();

  // Helper function to render competencies
  const renderCompetencies = (competencies) => {
    if (!competencies || !competencies.length) return null;
    
    return (
      <CompetenciesList>
        {competencies.map((comp, idx) => (
          <CompetencyItem key={idx}>{comp}</CompetencyItem>
        ))}
      </CompetenciesList>
    );
  };
  
  return (
    <DialogBox
      visible={visible}
      onClose={onClose}
      title="Assessment Questions Preview"
      width="900px"
      footer={
        <Button type="primary" onClick={onClose}>
          Close Preview
        </Button>
      }
    >
      <StyledTabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        type="card"
      >
        <TabPane 
          tab={
            <span>
              <FileTextOutlined /> Assessment Overview
            </span>
          } 
          key="metadata"
        >
          <AssessmentHeader>
            <AssessmentTitle>{meta.title || "Assessment"}</AssessmentTitle>
            <AssessmentMetadata>
              <MetadataItem>
                <MetadataLabel>Target Role:</MetadataLabel>
                <MetadataValue>{meta.target_role || "Not specified"}</MetadataValue>
              </MetadataItem>
              <MetadataItem>
                <MetadataLabel>Duration:</MetadataLabel>
                <MetadataValue>{calculatedDuration} minutes</MetadataValue>
              </MetadataItem>
              <MetadataItem>
                <MetadataLabel>Structure:</MetadataLabel>
                <MetadataValue>{meta.structure || "Not specified"}</MetadataValue>
              </MetadataItem>
              <MetadataItem>
                <MetadataLabel>Target Experience:</MetadataLabel>
                <MetadataValue>{meta.target_experience || "Not specified"}</MetadataValue>
              </MetadataItem>
            </AssessmentMetadata>
          </AssessmentHeader>
          
          <Section>
            <SectionHeader>
              <SectionTitle>Section A: Multiple Choice Questions</SectionTitle>
              <QuestionCount>
                {mcq.questions?.length || 0} Questions
              </QuestionCount>
            </SectionHeader>
            <SectionDescription>
              {questionTypeDescriptions.mcq || defaultQuestionTypeDescriptions.mcq}
            </SectionDescription>
          </Section>
          
          <Section>
            <SectionHeader>
              <SectionTitle>Section B: Written Response Questions</SectionTitle>
              <QuestionCount>
                {written.questions?.length || 0} Questions
              </QuestionCount>
            </SectionHeader>
            <SectionDescription>
              {questionTypeDescriptions.subjective || defaultQuestionTypeDescriptions.subjective}
            </SectionDescription>
          </Section>
          
          <Section>
            <SectionHeader>
              <SectionTitle>Section C: Video Response Question</SectionTitle>
              <QuestionCount>
                {Array.isArray(video.question) ? video.question.length : (video.question ? 1 : 0)} Question{(Array.isArray(video.question) && video.question.length > 1) ? 's' : ''}
              </QuestionCount>
            </SectionHeader>
            <SectionDescription>
              {questionTypeDescriptions.video || defaultQuestionTypeDescriptions.video}
            </SectionDescription>
          </Section>

          {coding.questions && coding.questions.length > 0 && (
            <Section noBorder>
              <SectionHeader>
                <SectionTitle>Section D: Coding Questions</SectionTitle>
                <QuestionCount>
                  {coding.questions?.length || 0} Questions
                </QuestionCount>
              </SectionHeader>
              <SectionDescription>
                {questionTypeDescriptions.coding || defaultQuestionTypeDescriptions.coding}
              </SectionDescription>
            </Section>
          )}
        </TabPane>
        
        <TabPane 
          tab={
            <span>
              <CheckCircleOutlined /> Multiple Choice ({mcq.questions?.length || 0})
            </span>
          } 
          key="mcq"
        >
          <Section>
            <SectionTitle>Section A: Multiple Choice Questions</SectionTitle>
            <SectionDescription>
              {mcq.instructions || questionTypeDescriptions.mcq || defaultQuestionTypeDescriptions.mcq}
            </SectionDescription>
            
            {mcq.questions?.map((q, idx) => (
              <QuestionItem key={idx}>
                <QuestionNumber>Question {idx + 1}</QuestionNumber>
                <QuestionText>{q.question}</QuestionText>
                
                <OptionsList>
                  {q.options?.map((option, optIdx) => {
                    const ca = (q.correct_answer || '').trim().toLowerCase();
                    const opt = (option || '').trim().toLowerCase();
                    const isCorrect = opt === ca
                      || ca.includes(opt)
                      || opt.includes(ca)
                      || ca.replace(/^[a-d]\)\s*/i, '') === opt
                      || ca.replace(/^option\s+[a-d][\):.\s]*/i, '').trim() === opt;
                    return (
                    <OptionItem
                      key={optIdx}
                      isCorrect={isCorrect}
                    >
                      {option}
                      {isCorrect && (
                        <CorrectBadge>Correct</CorrectBadge>
                      )}
                    </OptionItem>
                    );
                  })}
                </OptionsList>
                
                <MetadataList>
                  <MetadataTag>
                    <strong>Explanation:</strong> &nbsp;{q.explanation}
                  </MetadataTag>
                </MetadataList>
                
                <MetadataList>
                  <MetadataTag>
                    <strong>Competency:</strong> &nbsp;{q.competency_tested}
                  </MetadataTag>
                  <MetadataTag>
                    <strong>Difficulty:</strong> &nbsp;{q.difficulty}
                  </MetadataTag>
                  <MetadataTag>
                    <strong>Time:</strong> &nbsp;{q.time_allocation} min
                  </MetadataTag>
                </MetadataList>
              </QuestionItem>
            ))}
          </Section>
        </TabPane>
        
        <TabPane 
          tab={
            <span>
              <FileTextOutlined /> Written Response ({written.questions?.length || 0})
            </span>
          } 
          key="written"
        >
          <Section>
            <SectionTitle>Section B: Written Response Questions</SectionTitle>
            <SectionDescription>
              {written.instructions || questionTypeDescriptions.subjective || defaultQuestionTypeDescriptions.subjective}
            </SectionDescription>
            
            {written.questions?.map((q, idx) => (
              <QuestionItem key={idx}>
                <QuestionNumber>Question {idx + 1}</QuestionNumber>
                <QuestionScenario>{q.scenario}</QuestionScenario>
                <QuestionText>{q.question}</QuestionText>
                
                <MetadataList>
                  <MetadataTag>
                    <strong>Max Words:</strong> &nbsp;{q.max_words}
                  </MetadataTag>
                  <MetadataTag>
                    <strong>Time Limit:</strong> &nbsp;{q.time_limit} min
                  </MetadataTag>
                </MetadataList>
                
                <div style={{ marginTop: '12px' }}>
                  <strong>Competencies Assessed:</strong>
                  {renderCompetencies(q.competencies_assessed)}
                </div>
              </QuestionItem>
            ))}
          </Section>
        </TabPane>
        
        <TabPane
          tab={
            <span>
              <VideoCameraOutlined /> Video Response {(() => {
                const vq = video.question;
                if (!vq) return "(0)";
                if (Array.isArray(vq)) return `(${vq.length})`;
                return "(1)";
              })()}
            </span>
          }
          key="video"
        >
          <Section>
            <SectionTitle>Section C: Video Response Questions</SectionTitle>
            <SectionDescription>
              {video.instructions || questionTypeDescriptions.video || defaultQuestionTypeDescriptions.video}
            </SectionDescription>

            {video.question && (
              (() => {
                const videoQuestions = Array.isArray(video.question) ? video.question : [video.question];
                return videoQuestions.map((vq, vIdx) => (
                  <div key={vIdx} style={{
                    marginBottom: '28px',
                    padding: '20px',
                    background: '#fff',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: '1px solid #f1f5f9'
                    }}>
                      <div style={{
                        background: '#4318FF',
                        color: '#fff',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '14px',
                        marginRight: '12px',
                        flexShrink: 0
                      }}>
                        {vIdx + 1}
                      </div>
                      <QuestionNumber style={{ margin: 0 }}>Video Question {vIdx + 1}</QuestionNumber>
                    </div>
                    {vq.scenario && (
                      <QuestionScenario>{vq.scenario}</QuestionScenario>
                    )}
                    <QuestionText>{vq.question}</QuestionText>

                    <MetadataList>
                      <MetadataTag>
                        <strong>Time Limit:</strong> &nbsp;{vq.time_limit}
                      </MetadataTag>
                    </MetadataList>

                    <div style={{ marginTop: '12px' }}>
                      <strong>Competencies Assessed:</strong>
                      {renderCompetencies(vq.competencies_assessed)}
                    </div>
                  </div>
                ));
              })()
            )}

            {!video.question && (
              <div style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                No video response questions available.
              </div>
            )}
          </Section>
        </TabPane>

        {coding.questions && coding.questions.length > 0 && (
          <TabPane
            tab={
              <span>
                <CodeOutlined /> Coding ({coding.questions?.length || 0})
              </span>
            }
            key="coding"
          >
            <Section>
              <SectionTitle>Section D: Coding Questions</SectionTitle>
              <SectionDescription>
                {coding.instructions || questionTypeDescriptions.coding || defaultQuestionTypeDescriptions.coding}
              </SectionDescription>

              {coding.questions?.map((q, idx) => (
                <QuestionItem key={idx}>
                  <QuestionNumber>Question {idx + 1}: {q.title}</QuestionNumber>
                  <QuestionText>{q.problem_statement}</QuestionText>

                  {q.input_format && (
                    <div style={{ margin: '8px 0' }}>
                      <strong>Input Format:</strong>
                      <pre style={{ background: '#f1f5f9', padding: '8px', borderRadius: '4px', fontSize: '13px', marginTop: '4px' }}>{q.input_format}</pre>
                    </div>
                  )}
                  {q.output_format && (
                    <div style={{ margin: '8px 0' }}>
                      <strong>Output Format:</strong>
                      <pre style={{ background: '#f1f5f9', padding: '8px', borderRadius: '4px', fontSize: '13px', marginTop: '4px' }}>{q.output_format}</pre>
                    </div>
                  )}
                  {q.constraints && (
                    <div style={{ margin: '8px 0' }}>
                      <strong>Constraints:</strong>
                      <pre style={{ background: '#f1f5f9', padding: '8px', borderRadius: '4px', fontSize: '13px', marginTop: '4px' }}>{q.constraints}</pre>
                    </div>
                  )}

                  {q.examples && q.examples.length > 0 && (
                    <div style={{ margin: '12px 0' }}>
                      <strong>Examples:</strong>
                      {q.examples.map((ex, exIdx) => (
                        <div key={exIdx} style={{ background: '#f1f5f9', padding: '10px', borderRadius: '6px', marginTop: '6px', fontSize: '13px' }}>
                          <div><strong>Input:</strong> <code>{ex.input}</code></div>
                          <div><strong>Output:</strong> <code>{ex.output}</code></div>
                          {ex.explanation && <div style={{ color: '#64748b', marginTop: '4px' }}><strong>Explanation:</strong> {ex.explanation}</div>}
                        </div>
                      ))}
                    </div>
                  )}

                  <MetadataList>
                    <MetadataTag>
                      <strong>Difficulty:</strong> &nbsp;{q.difficulty}
                    </MetadataTag>
                    <MetadataTag>
                      <strong>Time:</strong> &nbsp;{q.time_allocation} min
                    </MetadataTag>
                    {q.test_cases && (
                      <MetadataTag>
                        <strong>Test Cases:</strong> &nbsp;{q.test_cases.length}
                      </MetadataTag>
                    )}
                  </MetadataList>
                </QuestionItem>
              ))}
            </Section>
          </TabPane>
        )}
      </StyledTabs>
    </DialogBox>
  );
};

export default QuestionDisplay;
